import defaultCaregiverPhoto from 'assets/experts-woman-light.png';
import { Button } from 'components/ui/button';
import { CaregiverIdentity, pickCaregivers, useRegisteredCaregivers } from 'hooks/useCaregivers';
import { cn } from 'lib/utils';
import { MessageCircleIcon, XIcon } from 'lucide-react';
import posthog from 'posthog-js';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

/**
 * How the first story's example messages are laid out (Figma 2a), by index: one
 * to the left, one to the right, two spread apart, one in the middle.
 */
const EXAMPLE_ROWS = [
  { indexes: [0], className: 'justify-start pl-2.5' },
  { indexes: [1], className: 'justify-end pr-6' },
  { indexes: [2, 3], className: 'justify-between' },
  { indexes: [4], className: 'justify-center' },
];

/** Where the team's portraits sit around the referent on the last story (Figma 2c), as % of the width. */
const PORTRAIT_SPOTS = [
  { left: -6, top: 60, size: 88 },
  { left: 22.6, top: 29, size: 56 },
  { left: 22.6, top: 111, size: 72 },
  { left: 65.9, top: 128, size: 56 },
  { left: 68.4, top: 0, size: 88 },
  { left: 86.5, top: 81, size: 104 },
] as const;

/**
 * "Online" dot per bubble size (px). The box includes its border (`border-box`);
 * the border is the paon background. `right`/`bottom` are from the bubble's
 * outer edge, so the dot's centre sits on the circle at 45°.
 */
const PASTILLE = {
  104: 'right-[8px] bottom-[8px] size-[16px] border-2',
  88: 'right-[6px] bottom-[6px] size-[14px] border-2',
  72: 'right-[4px] bottom-[4px] size-[12px] border-[2px]',
  56: 'right-[3px] bottom-[3px] size-[10px] border-[2px]',
} as const;

function Portrait({
  src,
  size,
  className,
  style,
}: {
  src: string;
  size: keyof typeof PASTILLE;
  className?: string;
  style?: object;
}) {
  return (
    <div className="absolute" style={{ width: size, height: size, ...style }}>
      <img src={src} alt="" className={cn('size-full rounded-full object-cover', className)} />
      <span className={cn('absolute rounded-full border-inverse bg-success', PASTILLE[size])} />
    </div>
  );
}

/**
 * The three stories introducing the messaging, full screen on the brand's
 * deep teal (Figma 2a/2b/2c-FirstTimeUse): what to ask, who answers, and the
 * team behind her.
 */
export default function MessagingStories({
  open,
  referent,
  onClose,
  onStart,
}: {
  open: boolean;
  referent: CaregiverIdentity;
  onClose: () => void;
  onStart: () => void;
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const { data: caregivers } = useRegisteredCaregivers();
  const team = pickCaregivers(caregivers?.filter(c => c.user.photo_url) ?? [], PORTRAIT_SPOTS.length);
  const teamPhotos = PORTRAIT_SPOTS.map((_, i) => team[i]?.user.photo_url || defaultCaregiverPhoto);
  const examples = t<string, { returnObjects: true }, string[]>('messagingV3.stories.question.examples', {
    returnObjects: true,
  });

  const stories = [
    {
      key: 'question',
      title: <Trans i18nKey="messagingV3.stories.question.title" components={{ br: <br /> }} />,
      text: t('messagingV3.stories.question.text'),
      illustration: (
        // Across the story's whole width, wider than its text
        <div className="-mx-4 flex flex-col gap-4" aria-hidden>
          {EXAMPLE_ROWS.map(row => (
            <div key={row.indexes.join()} className={cn('flex gap-4', row.className)}>
              {row.indexes
                .filter(i => examples[i])
                .map(i => (
                  <span
                    key={examples[i]}
                    className="flex h-9 items-center rounded-full bg-inverse-foreground/20 px-3 text-sm text-inverse-foreground"
                  >
                    {examples[i]}
                  </span>
                ))}
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'caregiver',
      title: t('messagingV3.stories.caregiver.title'),
      text: t('messagingV3.stories.caregiver.text'),
      illustration: (
        // The ring is centred on the portrait's edge
        <div className="relative mx-auto size-48 rounded-full outline-4 -outline-offset-2 outline-primary" aria-hidden>
          <img src={referent.photoUrl} alt="" className="size-full rounded-full object-cover" />
          <span className="absolute right-3 bottom-3 size-6 rounded-full border-4 border-inverse bg-success" />
        </div>
      ),
      // The portrait comes before the words on this one
      illustrationFirst: true,
    },
    {
      key: 'team',
      title: t('messagingV3.stories.team.title'),
      text: t('messagingV3.stories.team.text'),
      illustration: (
        <div className="relative h-48 w-full" aria-hidden>
          {PORTRAIT_SPOTS.map((spot, i) => (
            <Portrait key={i} src={teamPhotos[i]} size={spot.size} style={{ left: `${spot.left}%`, top: spot.top }} />
          ))}
          {referent.photoUrl && (
            <Portrait
              src={referent.photoUrl}
              size={104}
              className="border-4 border-primary"
              style={{ left: '41.2%', top: 33 }}
            />
          )}
        </div>
      ),
    },
  ];
  const story = stories[index];
  const isLast = index === stories.length - 1;

  // As in stories elsewhere, a tap on the right half of the screen moves on and
  // one on the left half goes back (its buttons keep their own action)
  const tapToNavigate = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button, a')) return;
    const { left, width } = event.currentTarget.getBoundingClientRect();
    if (event.clientX > left + width / 2) {
      if (!isLast) setIndex(index + 1);
    } else if (index > 0) {
      setIndex(index - 1);
    }
  };

  const close = () => {
    posthog.capture('messaging.stories_closed', { story: story.key });
    onClose();
    setIndex(0);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={nextOpen => !nextOpen && close()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          data-testid="messaging-stories"
          aria-describedby={undefined}
          // Full screen, over the bottom bar, below the device's safe areas
          className="fixed inset-0 z-[200] flex flex-col gap-6 overflow-x-hidden overflow-y-auto bg-inverse pt-safe-top pb-safe-bottom text-inverse-foreground"
          onClick={tapToNavigate}
        >
          <div className="mx-auto flex w-full max-w-content flex-1 flex-col gap-6 px-4 pt-4 pb-6">
            <div
              className="flex gap-1"
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={stories.length}
              aria-valuenow={index + 1}
              aria-label={t('messagingV3.stories.progress', { current: index + 1, total: stories.length })}
            >
              {stories.map((s, i) => (
                <span
                  key={s.key}
                  className={cn(
                    'h-1.5 flex-1 rounded-xs',
                    i <= index ? 'bg-inverse-foreground' : 'bg-muted-foreground',
                  )}
                />
              ))}
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold tracking-widest text-info-muted uppercase">
                {t('messagingV3.stories.label')}
              </p>
              <DialogPrimitive.Close asChild>
                <Button variant="ghost-inverse" size="icon-2xl" aria-label={t('messagingV3.stories.close')}>
                  <XIcon />
                </Button>
              </DialogPrimitive.Close>
            </div>

            <div
              className={cn(
                'flex flex-1 flex-col gap-12 px-4 pt-8',
                story.illustrationFirst && 'flex-col-reverse justify-end',
              )}
            >
              <div className="flex flex-col gap-4">
                <DialogPrimitive.Title asChild>
                  <h1 className="text-inverse-foreground">{story.title}</h1>
                </DialogPrimitive.Title>
                <p className="text-lg">{story.text}</p>
              </div>
              {story.illustration}
            </div>

            {isLast ? (
              <div className="flex flex-col items-center gap-4">
                <Button
                  size="xl"
                  className="w-full"
                  onClick={() => {
                    posthog.capture('messaging.stories_start_clicked');
                    onStart();
                    setIndex(0);
                  }}
                >
                  {t('messagingV3.startConversation')}
                  <MessageCircleIcon />
                </Button>
                <p className="text-xs text-inverse-foreground/80">{t('messagingV3.trustBadges')}</p>
                <Button variant="ghost-inverse" size="lg" onClick={close}>
                  {t('messagingV3.stories.close')}
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary-inverse"
                size="xl"
                className="w-56 self-center"
                onClick={() => setIndex(index + 1)}
              >
                {t('messagingV3.stories.continue')}
              </Button>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
